/**
 * @fileoverview Sign-in error extraction and display helpers for auth API responses.
 * @purpose Parse error/message and errorId/code from failed sign-in responses for UI and support.
 * @ownership Shell auth components
 * @see SignInFormContent.tsx
 */

/** Try to extract server error message and errorId from auth API response (for local/Docker debugging and support). */
export async function getServerErrorDetails(
  err: unknown
): Promise<{ message: string; errorId?: string } | null> {
  const readBody = async (
    res: Response
  ): Promise<{ message?: string; errorId?: string; error?: string } | null> => {
    try {
      const body = await res.json().catch(() => ({}));
      if (body && typeof body === 'object') {
        const msg =
          typeof (body as { message?: unknown }).message === 'string'
            ? (body as { message: string }).message
            : typeof (body as { error?: unknown }).error === 'string'
              ? (body as { error: string }).error
              : undefined;
        const errorId =
          typeof (body as { errorId?: unknown }).errorId === 'string'
            ? (body as { errorId: string }).errorId
            : typeof (body as { code?: unknown }).code === 'string'
              ? (body as { code: string }).code
              : undefined;
        return msg || errorId ? { message: msg ?? 'An error occurred', errorId } : null;
      }
    } catch {
      // ignore
    }
    return null;
  };
  // Duck-type Response (avoids instanceof issues in Jest/jsdom)
  if (err && typeof err === 'object' && typeof (err as Response).json === 'function') {
    const details = await readBody(err as Response);
    if (details) return { message: details.message ?? 'An error occurred', errorId: details.errorId };
  }
  if (err && typeof err === 'object' && 'response' in err) {
    const res = (err as { response?: Response }).response;
    if (res && typeof (res as Response).json === 'function') {
      const cloneable = typeof (res as Response).clone === 'function' ? (res as Response).clone() : (res as Response);
      const details = await readBody(cloneable);
      if (details) return { message: details.message ?? 'An error occurred', errorId: details.errorId };
    }
  }
  return null;
}

/** Format display string: message plus error code when present (e.g. "Service unavailable (ERR_INTERNAL_500_APP_19)"). */
export function formatErrorDisplay(message: string, errorId?: string): string {
  if (errorId && errorId.trim()) return `${message.trim()} (${errorId.trim()})`;
  return message;
}
